// ============= Full file contents =============

import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getSettings, updateSettings } from "@/lib/admin.functions";
import { generatePublicArchive, getPublicArchiveInfo } from "@/lib/archive.functions";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { toast } from "sonner";
import { BATCH_OPTIONS, DEFAULT_BATCH_SIZE, normalizeBatchSize } from "@/lib/batch-options";

type ModelOption = { id: string; in: number; out: number; note?: string };

/** Prices in USD per 1M tokens (input / output), from the AI Gateway catalog. */
const AVAILABLE_MODELS: ModelOption[] = [
  { id: "google/gemini-2.5-flash-lite", in: 0.1, out: 0.4, note: "legacy" },
  { id: "google/gemini-2.5-flash", in: 0.3, out: 2.5, note: "legacy" },
  { id: "google/gemini-2.5-pro", in: 1.25, out: 10, note: "legacy" },
  { id: "google/gemini-3.1-flash-lite", in: 0.25, out: 1.5 },
  { id: "google/gemini-3-flash-preview", in: 0.5, out: 3 },
  { id: "google/gemini-3.5-flash", in: 1.5, out: 9, note: "legacy" },
  { id: "google/gemini-3.6-flash", in: 0.75, out: 3.75 },
  { id: "google/gemini-3.7-flash", in: 0.75, out: 3.75 },
  { id: "google/gemini-3.8-flash", in: 0.75, out: 3.75 },
  { id: "google/gemini-3.1-pro-preview", in: 2, out: 12 },
  { id: "openai/gpt-5-nano", in: 0.05, out: 0.4 },
  { id: "openai/gpt-5.4-nano", in: 0.2, out: 1.25 },
  { id: "openai/gpt-5.6-luna", in: 0.2, out: 1.2 },
  { id: "openai/gpt-5.4-mini", in: 0.75, out: 4.5 },
  { id: "openai/gpt-5.6-terra", in: 2, out: 12 },
  { id: "openai/gpt-5.4", in: 2.5, out: 15 },
];

const fmtPrice = (n: number) => `$${n.toFixed(2)}`;

function ModelPrice({ m, muted }: { m: ModelOption; muted?: boolean }) {
  return (
    <span className={`font-mono text-[11px] tabular-nums ${muted ? "text-ink/40" : "text-amber-deep"}`}>
      {fmtPrice(m.in)} <span className="text-ink/30">in</span> · {fmtPrice(m.out)}{" "}
      <span className="text-ink/30">out</span>
    </span>
  );
}

/** Custom select so the price can sit right-aligned and de-emphasised. */
function ModelSelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const options = AVAILABLE_MODELS.some((m) => m.id === value) || !value
    ? AVAILABLE_MODELS
    : [{ id: value, in: 0, out: 0, note: "custom" } as ModelOption, ...AVAILABLE_MODELS];
  const current = options.find((m) => m.id === value);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <div ref={boxRef} className="relative">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="w-full ring-hairline bg-paper px-3 py-2.5 flex items-center gap-4 text-left"
      >
        <span className="flex-1 truncate">{current?.id ?? value}</span>
        {current && <ModelPrice m={current} muted />}
        <span className="text-ink/40 text-xs">▾</span>
      </button>

      {open && (
        <ul
          role="listbox"
          className="absolute z-20 left-0 right-0 mt-1 max-h-80 overflow-auto ring-hairline bg-paper shadow-lg"
        >
          {options.map((m) => (
            <li key={m.id}>
              <button
                type="button"
                role="option"
                aria-selected={m.id === value}
                onClick={() => {
                  onChange(m.id);
                  setOpen(false);
                }}
                className={`w-full px-3 py-2 flex items-center gap-4 text-left text-sm hover:bg-ink/5 ${
                  m.id === value ? "bg-ink/5" : ""
                }`}
              >
                <span className="flex-1 truncate">
                  {m.id}
                  {m.note && <span className="text-ink/30 text-xs ml-1.5">({m.note})</span>}
                </span>
                <ModelPrice m={m} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export const Route = createFileRoute("/_authenticated/admin/settings")({
  staticData: { sitemap: false },
  component: SettingsPage,
});

function SectionHeader({ title, hint, onSave, pending, dirty }: {
  title: string;
  hint: ReactNode;
  onSave: () => void;
  pending: boolean;
  dirty: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <div className="text-mono-caps text-ink/60">{title}</div>
        <p className="text-xs text-ink/50 mt-1 max-w-2xl">{hint}</p>
      </div>
      <button
        onClick={onSave}
        disabled={pending || !dirty}
        title={dirty ? "Save this section" : "No changes to save"}
        className="shrink-0 bg-ink text-paper px-4 py-2 text-sm hover:bg-amber transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
      >
        {pending ? "Saving…" : "Save"}
      </button>
    </div>
  );
}

function SettingsPage() {
  const qc = useQueryClient();
  const getFn = useServerFn(getSettings);
  const updFn = useServerFn(updateSettings);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "settings"],
    queryFn: () => getFn(),
  });

  const [model, setModel] = useState("");
  const [prompt, setPrompt] = useState("");
  const [contentPrompt, setContentPrompt] = useState("");
  const [travelPrompt, setTravelPrompt] = useState("");
  const [batchSize, setBatchSize] = useState<number>(DEFAULT_BATCH_SIZE);
  const [sttProvider, setSttProvider] = useState<"lovable" | "gemini" | "openai">("gemini");
  const [sttModel, setSttModel] = useState("gemini-2.5-pro");
  const [sttSecret, setSttSecret] = useState("GOOGLE_API_KEY");
  const [sttPrompt, setSttPrompt] = useState("");

  useEffect(() => {
    if (data) {
      setModel(data.llm_model);
      setPrompt(data.llm_prompt);
      setContentPrompt((data as any).llm_prompt_content ?? "");
      setTravelPrompt((data as any).llm_prompt_travel ?? "");
      setBatchSize(normalizeBatchSize(data.import_batch_size));
      setSttProvider(((data as any).stt_provider ?? "gemini") as "lovable" | "gemini" | "openai");
      setSttModel((data as any).stt_model ?? "gemini-2.5-pro");
      setSttSecret((data as any).stt_api_key_secret ?? "GOOGLE_API_KEY");
      setSttPrompt((data as any).stt_prompt ?? "");
    }
  }, [data]);

  const makeMut = (section: string) =>
    useMutation({
      mutationFn: (patch: any) => updFn({ data: patch }),
      onSuccess: () => {
        toast.success(`${section} saved`);
        qc.invalidateQueries({ queryKey: ["admin", "settings"] });
      },
      onError: (e: Error) => toast.error(e.message),
    });

  const promptsMut = makeMut("Model & prompts");
  const transcriptionMut = makeMut("Transcription");
  const importsMut = makeMut("Import defaults");

  if (isLoading || !data) return <div className="p-10 text-ink/40">Loading…</div>;

  return (
    <div className="p-10">
      <p className="text-mono-caps text-amber mb-3">Configuration</p>
      <h1 className="text-4xl mb-2">Settings</h1>
      <p className="text-sm text-ink/50 mb-8">
        Configuration of the backoffice automated processes: the shared language model,
        the prompts for each step, video transcription, and the default import values.
      </p>

      <div className="space-y-8">
        {/* ---------- 1. LLM + prompts ---------- */}
        <section className="ring-hairline bg-paper p-5 space-y-4">
          <SectionHeader
            title="1 · Language model & prompts"
            hint={
              <>
                All three prompts below run on this single model: <strong>geolocation</strong>,{" "}
                <strong>content processing</strong> (clean description + summary) and{" "}
                <strong>travel classification</strong>. Transcription uses its own model (section 2).
              </>
            }
            onSave={() =>
              promptsMut.mutate({
                llm_model: model,
                llm_prompt: prompt,
                llm_prompt_content: contentPrompt,
                llm_prompt_travel: travelPrompt,
              })
            }
            pending={promptsMut.isPending}
            dirty={
              model !== data.llm_model ||
              prompt !== data.llm_prompt ||
              contentPrompt !== ((data as any).llm_prompt_content ?? "") ||
              travelPrompt !== ((data as any).llm_prompt_travel ?? "")
            }
          />

          <div>
            <label className="text-mono-caps text-ink/50 block mb-2">LLM model</label>
            <ModelSelect value={model} onChange={setModel} />
            <p className="text-xs text-ink/50 mt-1.5">
              Prices are USD per 1M tokens (input / output) on the Lovable AI Gateway.
              Flash/nano models are fast and cheap; Pro models are more accurate.
            </p>
          </div>

          <div>
            <label className="text-mono-caps text-ink/50 block mb-2">
              Geolocation prompt <span className="text-ink/30">— extract locations</span>
            </label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={5}
              className="w-full ring-hairline bg-paper px-3 py-2.5 font-mono text-xs resize-y"
            />
          </div>

          <div>
            <label className="text-mono-caps text-ink/50 block mb-2">
              Content processing prompt <span className="text-ink/30">— clean description + summary</span>
            </label>
            <textarea
              value={contentPrompt}
              onChange={(e) => setContentPrompt(e.target.value)}
              rows={5}
              className="w-full ring-hairline bg-paper px-3 py-2.5 font-mono text-xs resize-y"
            />
          </div>

          <div>
            <label className="text-mono-caps text-ink/50 block mb-2">
              Travel classification prompt <span className="text-ink/30">— used by "Hide non-travel"</span>
            </label>
            <textarea
              value={travelPrompt}
              onChange={(e) => setTravelPrompt(e.target.value)}
              rows={5}
              className="w-full ring-hairline bg-paper px-3 py-2.5 font-mono text-xs resize-y"
            />
            <p className="text-xs text-ink/50 mt-1.5">
              Must return strict JSON: {"{"}"is_travel": true|false, "reason": "…"{"}"}.
            </p>
          </div>
        </section>

        {/* ---------- 2. Transcription ---------- */}
        <section className="ring-hairline bg-paper p-5 space-y-4">
          <SectionHeader
            title="2 · Video transcription"
            hint={
              <>
                Used to generate the transcription when YouTube returns no subtitles (and by the
                "Transcribe" button in the backoffice). Independent from the model in section 1.
              </>
            }
            onSave={() =>
              transcriptionMut.mutate({
                stt_provider: sttProvider,
                stt_model: sttModel,
                stt_api_key_secret: sttSecret,
                stt_prompt: sttPrompt,
              })
            }
            pending={transcriptionMut.isPending}
            dirty={
              sttProvider !== ((data as any).stt_provider ?? "gemini") ||
              sttModel !== ((data as any).stt_model ?? "gemini-2.5-pro") ||
              sttSecret !== ((data as any).stt_api_key_secret ?? "GOOGLE_API_KEY") ||
              sttPrompt !== ((data as any).stt_prompt ?? "")
            }
          />

          <div>
            <label className="text-mono-caps text-ink/50 block mb-2">Provider</label>
            <select
              value={sttProvider}
              onChange={(e) => {
                const v = e.target.value as "lovable" | "gemini" | "openai";
                setSttProvider(v);
                if (v === "gemini") {
                  if (!sttSecret || sttSecret === "OPENAI_API_KEY") setSttSecret("GOOGLE_API_KEY");
                  if (!sttModel || /gpt|whisper/i.test(sttModel)) setSttModel("gemini-2.5-pro");
                } else if (v === "openai") {
                  if (!sttSecret || sttSecret === "GOOGLE_API_KEY") setSttSecret("OPENAI_API_KEY");
                  if (!sttModel || /gemini/i.test(sttModel)) setSttModel("openai/gpt-4o-transcribe");
                }
              }}
              className="w-full ring-hairline bg-paper px-3 py-2.5"
            >
              <option value="gemini">Gemini direct — Google's own API (working)</option>
              <option value="lovable">Lovable AI Gateway (not supported for video)</option>
              <option value="openai">OpenAI Whisper (audio, pipeline not wired)</option>
            </select>
            {sttProvider === "lovable" && (
              <p className="text-xs text-amber-deep mt-1.5">
                ⚠ The Lovable Gateway does not accept YouTube URLs in chat-completions. Select "Gemini direct" to transcribe with your <code>GOOGLE_API_KEY</code> key.
              </p>
            )}
            {sttProvider === "openai" && (
              <p className="text-xs text-amber-deep mt-1.5">
                ⚠ The audio route (extraction + STT) is not wired yet. Select "Gemini direct" for now.
              </p>
            )}
          </div>

          <div>
            <label className="text-mono-caps text-ink/50 block mb-2">Model</label>
            <input
              value={sttModel}
              onChange={(e) => setSttModel(e.target.value)}
              className="w-full ring-hairline bg-paper px-3 py-2.5 font-mono text-xs"
            />
            <p className="text-xs text-ink/50 mt-1.5">
              Gemini: <code>gemini-2.5-pro</code>, <code>gemini-2.5-flash</code>. OpenAI: <code>openai/gpt-4o-transcribe</code>, <code>whisper-1</code>.
            </p>
          </div>

          <div>
            <label className="text-mono-caps text-ink/50 block mb-2">API key secret name</label>
            <input
              value={sttSecret}
              onChange={(e) => setSttSecret(e.target.value)}
              className="w-full ring-hairline bg-paper px-3 py-2.5 font-mono text-xs"
            />
            <p className="text-xs text-ink/50 mt-1.5">
              Name of the environment variable holding the key. For Gemini: <code>GOOGLE_API_KEY</code>. Add the key to the backend as a secret with this exact name.
            </p>
          </div>

          <div>
            <label className="text-mono-caps text-ink/50 block mb-2">Transcription system prompt</label>
            <textarea
              value={sttPrompt}
              onChange={(e) => setSttPrompt(e.target.value)}
              rows={4}
              className="w-full ring-hairline bg-paper px-3 py-2.5 font-mono text-xs resize-y"
            />
          </div>
        </section>

        {/* ---------- 3. Imports ---------- */}
        <section className="ring-hairline bg-paper p-5 space-y-4">
          <SectionHeader
            title="3 · Imports and batch actions"
            hint='Value preselected in the "Batch actions" selector on each channel page.'
            onSave={() => importsMut.mutate({ import_batch_size: batchSize })}
            pending={importsMut.isPending}
            dirty={batchSize !== normalizeBatchSize(data.import_batch_size)}
          />
          <div>
            <label className="text-mono-caps text-ink/50 block mb-2">Default batch size</label>
            <select
              value={batchSize}
              onChange={(e) => setBatchSize(Number(e.target.value))}
              className="w-32 ring-hairline bg-paper px-3 py-2.5"
            >
              {BATCH_OPTIONS.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
        </section>

        <PublicArchiveSection />
      </div>
    </div>
  );
}

function PublicArchiveSection() {
  const qc = useQueryClient();
  const infoFn = useServerFn(getPublicArchiveInfo);
  const genFn = useServerFn(generatePublicArchive);
  const { data: info } = useQuery({ queryKey: ["admin", "archive-info"], queryFn: () => infoFn() });
  const gen = useMutation({
    mutationFn: () => genFn(),
    onSuccess: (r) => {
      toast.success(`Archive generated: ${r.videos} videos, ${(r.gzip_bytes / 1024).toFixed(0)} KB compressed`);
      qc.invalidateQueries({ queryKey: ["admin", "archive-info"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
  return (
    <section className="ring-hairline bg-paper p-5 space-y-4">
      <div>
        <h2 className="text-xl">4 · Public archive</h2>
        <p className="text-sm text-ink/50 mt-1">
          A single compressed file with every visible video. Visitors download it once and the public
          site (lists, filters, maps, search) runs instantly from it. Regenerate after importing or editing videos.
        </p>
      </div>
      <div className="flex items-center gap-4 flex-wrap">
        <button
          onClick={() => gen.mutate()}
          disabled={gen.isPending}
          className="bg-ink text-paper px-4 py-2.5 text-sm hover:bg-amber transition-colors disabled:opacity-50"
        >
          {gen.isPending ? "Generating…" : "Generate public archive"}
        </button>
        <span className="text-sm text-ink/60">
          {info
            ? `Last generated: ${new Date(info.updated_at).toLocaleString("en-GB")}${
                info.gzip_bytes ? ` · ${(info.gzip_bytes / 1024).toFixed(0)} KB` : ""
              }`
            : "Not generated yet — the site reads from the database."}
        </span>
      </div>
    </section>
  );
}
